import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import type { SpassuCodeApi } from '../../api';
import type { SearchWorkItemsFilters } from '../../api/types';
import type { UseProjectRoleResult } from '../../hooks/useProjectRole';
import type { ActivityCreationContentAction } from '../../activity-creation/activityCreationReducer';
import { useWorkItemSelection } from './useWorkItemSelection';

export type CreationMode = 'blank' | 'work-item';
interface BoardContext {
  api: SpassuCodeApi;
  open: boolean;
  provider?: string;
  project: string;
  permissions: UseProjectRoleResult;
  dispatch: Dispatch<ActivityCreationContentAction>;
  setError: (error: string) => void;
}

export function useCreationBoard(context: BoardContext) {
  const config = useApi(configApiRef);
  const enabled =
    config.getOptionalBoolean('spassuCode.features.azureBoards') ?? false;
  const [mode, setMode] = useState<CreationMode>('work-item');
  const { api, open, provider, project, permissions, dispatch, setError } =
    context;
  const workItem = useWorkItemSelection(
    api,
    { open, provider, project, mode },
    dispatch,
    setError,
  );
  const { clearWorkItem } = workItem;
  const { getRolesForProject, isAdmin } = permissions;
  const roles = useMemo(
    () => (project ? getRolesForProject(project) : []),
    [project, getRolesForProject],
  );
  const canCreateBlank =
    isAdmin || roles.includes('gestor') || roles.includes('analista');
  const initialFilters = useMemo<Partial<SearchWorkItemsFilters>>(
    () => (canCreateBlank ? {} : { assignedToMe: true }),
    [canCreateBlank],
  );
  useEffect(() => {
    setMode(enabled ? 'work-item' : 'blank');
  }, [project, enabled]);
  const changeMode = useCallback(
    (next: CreationMode) => {
      if (next === 'blank') clearWorkItem();
      setMode(next);
    },
    [clearWorkItem],
  );
  const reset = () => {
    clearWorkItem();
    setMode(enabled ? 'work-item' : 'blank');
  };
  return {
    ...workItem,
    enabled,
    mode,
    canCreateBlank,
    initialFilters,
    changeMode,
    reset,
    showSection: Boolean(enabled && project && mode === 'work-item'),
  };
}
